const bindConditionGuide = section => {
  const root = section.querySelector('.sp-condition-guide');
  if (!root || root.dataset.spConditionReady === 'true') return;
  const messages = JSON.parse(root.querySelector('[data-sp-condition-messages]')?.textContent || '{}');
  const descriptions = messages.descriptions || {};
  const render = () => {
    const values = [...section.querySelectorAll('variant-picker input:checked, variant-picker select')].map(input => input.value.split(' · ')[0].trim());
    const condition = values.find(value => descriptions[value]) || root.dataset.initialCondition;
    const copy = root.querySelector('[data-sp-condition-copy]');
    if (copy) copy.textContent = descriptions[condition] || messages.fallback || '';
  };
  root.dataset.spConditionReady = 'true';
  section.addEventListener('change', () => requestAnimationFrame(render));
  render();
};

class ShopyphoneSimSelector extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.restore);
    this.restore();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('popstate', this.restore);
    this.controller?.abort();
  }

  restore = () => {
    const url = new URL(location.href);
    const requested = url.searchParams.get('sim') || (url.pathname.endsWith('-psim') ? 'physical-sim' : 'esim-only');
    const target = [...this.querySelectorAll('button[data-sim]')].find(button => button.dataset.sim === requested);
    if (target && !target.disabled && requested !== this.dataset.sim) {
      this.select(target, false, url.searchParams.get('variant'));
    }
  };

  onClick = event => {
    const button = event.target.closest('button[data-sim]');
    if (button && !button.disabled && button.dataset.sim !== this.dataset.sim) this.select(button, true);
  };

  async select(button, updateHistory, requestedVariant) {
    if (this.loading) return;
    const section = document.getElementById('shopify-section-' + this.dataset.sectionId);
    if (!section) return;
    this.loading = true;
    this.controller = new AbortController();
    const status = this.querySelector('[role="status"]');
    const controls = [...section.querySelectorAll('button, input, select')].map(element => [element, element.disabled]);
    const selected = [...section.querySelectorAll('variant-picker input:checked, variant-picker select')].map(input => input.value);
    const originalY = this.getBoundingClientRect().top;
    status.textContent = '';
    section.setAttribute('aria-busy', 'true');
    section.inert = true;
    for (const [element] of controls) element.disabled = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const productUrl = new URL(root + 'products/' + button.dataset.handle, location.origin);
      const response = await fetch(productUrl.pathname + '.js', { signal: this.controller.signal });
      if (!response.ok) throw new Error('Product unavailable');
      const product = await response.json();
      const variant = product.variants.find(item => String(item.id) === requestedVariant)
        || product.variants.find(item => selected.length === item.options.length && item.options.every((value, index) => value === selected[index]))
        || product.variants.find(item => item.available)
        || product.variants[0];
      if (!variant) throw new Error('Missing variant');
      productUrl.searchParams.set('variant', variant.id);
      productUrl.searchParams.set('section_id', this.dataset.sectionId);
      const rendered = await fetch(productUrl, { signal: this.controller.signal });
      if (!rendered.ok) throw new Error('Section unavailable');
      const documentNext = new DOMParser().parseFromString(await rendered.text(), 'text/html');
      const next = documentNext.getElementById(section.id);
      const selector = next?.querySelector('shopyphone-sim-selector');
      const formId = next?.querySelector('form[action*="/cart/add"] input[name="id"]')?.value;
      if (!selector || selector.dataset.sim !== button.dataset.sim || formId !== String(variant.id)) throw new Error('Configuration mismatch');
      const url = new URL(location.href);
      url.searchParams.set('sim', button.dataset.sim);
      url.searchParams.set('variant', variant.id);
      if (updateHistory) history.pushState({}, '', url);
      else history.replaceState({}, '', url);
      section.replaceWith(next);
      bindConditionGuide(next);
      const current = document.getElementById(next.id).querySelector('shopyphone-sim-selector');
      if (updateHistory) {
        current.querySelector('[aria-pressed="true"]').focus({ preventScroll: true });
        window.scrollBy(0, current.getBoundingClientRect().top - originalY);
      }
      document.dispatchEvent(new CustomEvent('shopyphone:sim-change', { detail: { sim: button.dataset.sim, variantId: variant.id } }));
    } catch (error) {
      if (error.name !== 'AbortError') status.textContent = this.dataset.error;
      if (!updateHistory) {
        const url = new URL(location.href);
        url.searchParams.set('sim', this.dataset.sim);
        url.searchParams.delete('variant');
        history.replaceState({}, '', url);
      }
    } finally {
      if (section.isConnected) {
        section.inert = false;
        section.removeAttribute('aria-busy');
        for (const [element, disabled] of controls) element.disabled = disabled;
      }
      this.loading = false;
    }
  }
}

if (!customElements.get('shopyphone-sim-selector')) customElements.define('shopyphone-sim-selector', ShopyphoneSimSelector);
